/**
 * Шахматка номеров — главный экран раздела «Номера» менеджера.
 * Построен ПО ДИЗАЙНУ manager v3 §4.1 (шахматка) + §4.4 (Timeline view):
 *
 *   • Календарная сетка строки=номера × столбцы=дни (горизонт 14 дней).
 *   • Цветовые статусы ячеек: свободно / занят / hold / грязный (палитра v3).
 *   • Брони как непрерывные полосы по диапазону дат (Timeline-режим).
 *   • Toggle «Шахматка / Timeline» (segmented control, top-right).
 *   • Навигация по периоду ← / → (шаг = размер окна), «Сегодня».
 *   • Легенда статусов внизу.
 *   • Sticky первая колонка (номер · тип).
 *   • Сегодня — подсвеченная колонка; выходные — tinted (§4.4 v3).
 *   • Мобайл 375: сетка нечитаема → vertical day-view (room-cards на
 *     выбранный день, day-pick ← →), как в макете §4.1 phone.
 *
 * Данные: GET /v1/rooms/calendar (bulk occupancies) — единственный источник;
 * имена типов — публичный каталог. i18n полный: статусы мапятся в русский
 * (chessboard-format), сырых англ-кодов нет.
 *
 * Имя гостя берём из occupancy.guest_name (CalendarOccupancyResponse его
 * отдаёт) — в полосе timeline показываем ФИО, fallback на статус. Клик по
 * полосе/мобайл-карточке открывает карточку проживания (StayDetail).
 */
import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Skeleton } from '@hsc/ui';
import type { CalendarOccupancy, CalendarRoom } from '@hsc/types';
import { resolveTenantSlug } from '../../lib/tenant-slug.js';
import { isoDate } from '../../lib/fleet-format.js';
import {
  buildDays,
  useChessboardCalendar,
  useRoomTypeNames,
  type DayColumn,
} from './useChessboard.js';
import {
  occupancyLabel,
  occupancyTone,
  roomBaseLabel,
  roomBaseTone,
  type CellTone,
} from './chessboard-format.js';
import { StayDetail } from '../stays/StayDetail.js';
import { BookingForm } from './BookingForm.js';

/** Контекст открываемого проживания (из occupancy + строки номера). */
interface SelectedStay {
  stayId: string;
  roomNumber: string;
  guestName: string;
}

/** Горизонт отображения (дней). 14 — desktop-first из §4.1. */
const WINDOW_DAYS = 30;

type ViewMode = 'board' | 'timeline';

interface BarSegment {
  startIdx: number;
  span: number;
  tone: 'busy' | 'hold';
  label: string;
  stayId: string;
  guestName: string;
}

/** День попадает в occupancy, если from <= day < to (половинно-открытый). */
function dayInOccupancy(dayIso: string, occ: CalendarOccupancy): boolean {
  const day = dayIso;
  const from = occ.from.slice(0, 10);
  const to = occ.to.slice(0, 10);
  return day >= from && day < to;
}

/** Брони комнаты как непрерывные полосы в координатах колонок дней. */
function buildBars(room: CalendarRoom, days: DayColumn[]): BarSegment[] {
  const bars: BarSegment[] = [];
  for (const occ of room.occupancies) {
    let startIdx = -1;
    for (let i = 0; i < days.length; i += 1) {
      const inside = dayInOccupancy(days[i]!.iso, occ);
      if (inside && startIdx === -1) startIdx = i;
    }
    if (startIdx === -1) continue;
    // span — число подряд идущих дней окна, входящих в бронь.
    let span = 0;
    for (let i = startIdx; i < days.length; i += 1) {
      if (dayInOccupancy(days[i]!.iso, occ)) span += 1;
      else break;
    }
    bars.push({
      startIdx,
      span,
      tone: occupancyTone(occ.status),
      label: occupancyLabel(occ.status),
      stayId: occ.stay_id,
      guestName: occ.guest_name ?? '',
    });
  }
  return bars.sort((a, b) => a.startIdx - b.startIdx);
}

/**
 * Тон конкретной ячейки (день × комната) в режиме «Шахматка».
 *
 * Приоритет: бронь на этот день → её статус. Иначе ячейка свободна. Текущий
 * физический статус номера (уборка / не в строю) описывает ТОЛЬКО «сегодня»
 * — нет по-дневной housekeeping-проекции в calendar-эндпойнте, поэтому
 * красим им лишь сегодняшнюю колонку (иначе номер «на уборке» залил бы все
 * 14 дней вперёд — это вводит в заблуждение).
 */
function cellTone(
  room: CalendarRoom,
  dayIso: string,
  isToday: boolean,
): { tone: CellTone; label: string } {
  for (const occ of room.occupancies) {
    if (dayInOccupancy(dayIso, occ)) {
      return { tone: occupancyTone(occ.status), label: occupancyLabel(occ.status) };
    }
  }
  if (isToday) {
    const base = roomBaseTone(room.status);
    if (base !== 'free') return { tone: base, label: roomBaseLabel(room.status) };
  }
  return { tone: 'free', label: '' };
}

export interface RoomsChessboardProps {
  /** Управляющая роль — показывает CTA «+ Бронь» (создание брони — TODO). */
  canManage?: boolean;
}

export function RoomsChessboard({ canManage = false }: RoomsChessboardProps): JSX.Element {
  const slug = useMemo(() => resolveTenantSlug(), []);
  const [view, setView] = useState<ViewMode>('board');
  const [offset, setOffset] = useState(0); // смещение окна в днях
  // Мобайл day-view: индекс выбранного дня в окне.
  const [mobileDayIdx, setMobileDayIdx] = useState(0);
  // Открытое проживание (карточка) — по клику на полосу/занятую ячейку.
  const [selected, setSelected] = useState<SelectedStay | null>(null);
  // Форма создания брони «+ Бронь».
  const [bookingOpen, setBookingOpen] = useState(false);
  // Ссылка на горизонтальный скролл-контейнер сетки + запомненная позиция
  // прокрутки. При «проваливании» в бронь и возврате (#8) восстанавливаем
  // scrollLeft, чтобы менеджер вернулся к тому же дню/номеру, что был открыт.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const savedScrollLeft = useRef(0);
  // true, пока карточка/форма открыта — чтобы при возврате (selected→null)
  // восстановить scrollLeft ровно один раз после ре-рендера сетки.
  const wasDetailOpen = useRef(false);

  // Фиксируем позицию прокрутки в момент нажатия (pointerdown срабатывает
  // ДО того, как фокус на кнопке-ячейке авто-проскроллит контейнер). Так
  // savedScrollLeft хранит реальную позицию, которую видел менеджер (#8).
  const rememberScroll = (): void => {
    savedScrollLeft.current = scrollRef.current?.scrollLeft ?? 0;
  };

  /** Открыть карточку проживания (#7). Позиция уже сохранена на pointerdown. */
  const openStay = (stay: SelectedStay): void => {
    wasDetailOpen.current = true;
    setSelected(stay);
  };

  // #8: при возврате из карточки восстановить прежнюю прокрутку сетки —
  // менеджер видит тот же день/номер, что был открыт. useLayoutEffect
  // выполняется синхронно после commit DOM (до отрисовки), без мигания.
  useLayoutEffect(() => {
    if (selected === null && wasDetailOpen.current) {
      wasDetailOpen.current = false;
      if (scrollRef.current) scrollRef.current.scrollLeft = savedScrollLeft.current;
    }
  }, [selected]);

  const window = useMemo(() => {
    const from = new Date();
    from.setHours(0, 0, 0, 0);
    from.setDate(from.getDate() + offset);
    const to = new Date(from);
    to.setDate(to.getDate() + WINDOW_DAYS);
    return { from: isoDate(from), to: isoDate(to) };
  }, [offset]);

  const days = useMemo(() => buildDays(window.from, window.to), [window]);
  const { data, isLoading, isError } = useChessboardCalendar(window.from, window.to);
  const typesQ = useRoomTypeNames(slug);

  const typeName = useMemo(() => {
    const map = new Map<string, string>();
    for (const t of typesQ.data?.room_types ?? []) map.set(t.id, t.name);
    return map;
  }, [typesQ.data]);

  const periodLabel = useMemo(() => {
    const d = new Date(`${window.from}T00:00:00`);
    return d.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' });
  }, [window]);

  // Номера по числовому порядку (1→12), а не по тексту («1,10,11,12,2…»).
  const rooms = [...(data?.rooms ?? [])].sort(
    (a, b) => (parseInt(a.number, 10) || 0) - (parseInt(b.number, 10) || 0),
  );
  const safeMobileIdx = Math.min(mobileDayIdx, Math.max(0, days.length - 1));
  const mobileDay = days[safeMobileIdx];

  // «+ Бронь» — ОТДЕЛЬНЫЙ экран (форма вместо шахматки), не модалка поверх
  // (feedback_every_form_is_a_separate_screen). Возврат — «Назад» снизу формы.
  if (bookingOpen && canManage) {
    return (
      <section data-testid="rooms-chessboard">
        <BookingForm
          rooms={rooms}
          typeName={typeName}
          defaultFrom={mobileDay?.iso ?? window.from}
          onClose={() => setBookingOpen(false)}
          onCreated={() => setBookingOpen(false)}
        />
      </section>
    );
  }

  // Карточка проживания — ОТДЕЛЬНЫЙ экран (вместо шахматки), не оверлей поверх
  // (feedback_every_form_is_a_separate_screen). «Назад» возвращает к шахматке.
  if (selected !== null) {
    return (
      <section data-testid="rooms-chessboard">
        <StayDetail
          stayId={selected.stayId}
          canManage={canManage}
          roomNumber={selected.roomNumber}
          guestName={selected.guestName}
          onClose={() => setSelected(null)}
        />
      </section>
    );
  }

  return (
    <section data-testid="rooms-chessboard">
      {/* Topbar: заголовок + toggle + навигация (§4.1) */}
      <div className="cb-topbar">
        <div>
          <div className="cb-crumbs">Номера / {view === 'board' ? 'Шахматка' : 'Timeline'}</div>
          <h1 className="hsc-page__title cb-title">Загрузка отеля — {periodLabel}</h1>
        </div>
        <div className="cb-controls">
          <div className="cb-segmented" role="tablist" aria-label="Вид календаря">
            <button
              type="button"
              role="tab"
              aria-selected={view === 'board'}
              className={`cb-seg${view === 'board' ? ' is-active' : ''}`}
              onClick={() => setView('board')}
              data-testid="cb-view-board"
            >
              Шахматка
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={view === 'timeline'}
              className={`cb-seg${view === 'timeline' ? ' is-active' : ''}`}
              onClick={() => setView('timeline')}
              data-testid="cb-view-timeline"
            >
              Timeline
            </button>
          </div>
          <button
            type="button"
            className="fleet-chip"
            onClick={() => setOffset((o) => o - WINDOW_DAYS)}
            data-testid="cb-prev"
            aria-label="Предыдущий период"
          >
            ← Раньше
          </button>
          <button
            type="button"
            className="fleet-chip"
            onClick={() => setOffset(0)}
            data-testid="cb-today"
          >
            Сегодня
          </button>
          <button
            type="button"
            className="fleet-chip"
            onClick={() => setOffset((o) => o + WINDOW_DAYS)}
            data-testid="cb-next"
            aria-label="Следующий период"
          >
            Позже →
          </button>
          {canManage && (
            <button
              type="button"
              className="fleet-chip is-active"
              data-testid="cb-add-booking"
              onClick={() => setBookingOpen(true)}
            >
              + Бронь
            </button>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="cb-skeleton" data-testid="cb-skeleton">
          {Array.from({ length: 8 }, (_, i) => (
            <Skeleton key={i} height={28} radius={6} />
          ))}
        </div>
      ) : isError || !data ? (
        <div className="cb-banner cb-banner--error" role="alert" data-testid="cb-error">
          Не удалось загрузить календарь номеров. Попробуйте обновить страницу.
        </div>
      ) : rooms.length === 0 ? (
        <div className="cb-banner" data-testid="cb-empty">
          В этом периоде номеров и броней нет — добавьте первую бронь.
        </div>
      ) : (
        <>
          {/* ===== Desktop / tablet: сетка комната × дни ===== */}
          <div
            className="cb-scroll"
            data-testid="cb-grid-scroll"
            ref={scrollRef}
            onPointerDownCapture={rememberScroll}
          >
            <div
              className="cb-grid"
              style={{ gridTemplateColumns: `var(--cb-room-col) repeat(${days.length}, var(--cb-day-col))` }}
              role="table"
              aria-label="Шахматка номеров"
            >
              {/* Шапка дней */}
              <div className="cb-cell cb-head cb-room-col" role="columnheader">
                Номер
              </div>
              {days.map((d) => (
                <div
                  key={d.iso}
                  className={`cb-cell cb-head${d.isToday ? ' is-today' : ''}${d.isWeekend ? ' is-weekend' : ''}`}
                  role="columnheader"
                  data-testid={`cb-day-${d.iso}`}
                >
                  <span className="cb-head__wd">{d.weekday}</span>
                  <span className="cb-head__date">{d.short}</span>
                  {d.isToday && <span className="cb-head__today">СЕГОДНЯ</span>}
                </div>
              ))}

              {/* Строки номеров */}
              {rooms.map((room) => {
                const tname = typeName.get(room.type_id);
                return (
                  <RoomRow
                    key={room.id}
                    room={room}
                    typeName={tname}
                    days={days}
                    view={view}
                    onOpenStay={(stay) =>
                      openStay({
                        stayId: stay.stayId,
                        roomNumber: room.number,
                        guestName: stay.guestName,
                      })
                    }
                  />
                );
              })}
            </div>
          </div>

          {/* ===== Мобайл 375: vertical day-view ===== */}
          <div className="cb-mobile" data-testid="cb-mobile">
            <div className="cb-mobile__nav">
              <button
                type="button"
                className="fleet-chip"
                onClick={() => setMobileDayIdx((i) => Math.max(0, i - 1))}
                aria-label="Предыдущий день"
                data-testid="cb-mobile-prev"
              >
                ←
              </button>
              <div className="cb-mobile__day">
                {mobileDay ? `${mobileDay.weekday} ${mobileDay.short}` : '—'}
              </div>
              <button
                type="button"
                className="fleet-chip"
                onClick={() => setMobileDayIdx((i) => Math.min(days.length - 1, i + 1))}
                aria-label="Следующий день"
                data-testid="cb-mobile-next"
              >
                →
              </button>
            </div>
            <ul className="cb-mobile__list">
              {rooms.map((room) => {
                const { tone, label } = mobileDay
                  ? cellTone(room, mobileDay.iso, mobileDay.isToday)
                  : { tone: 'free' as CellTone, label: '' };
                const tname = typeName.get(room.type_id);
                const occ = mobileDay
                  ? room.occupancies.find((o) => dayInOccupancy(mobileDay.iso, o))
                  : undefined;
                const open = (): void => {
                  if (!occ) return;
                  openStay({
                    stayId: occ.stay_id,
                    roomNumber: room.number,
                    guestName: occ.guest_name ?? '',
                  });
                };
                return (
                  <li key={room.id}>
                    <button
                      type="button"
                      className={`cb-mcard cb-cell--${tone}${occ ? ' is-clickable' : ''}`}
                      data-testid={`cb-mcard-${room.number}`}
                      onClick={open}
                      disabled={!occ}
                    >
                      <strong className="cb-mcard__no">
                        {room.number}
                        {tname ? ` · ${tname}` : ''}
                      </strong>
                      <span className="cb-mcard__status">
                        {tone === 'free' ? 'Свободно' : label}
                        {occ?.guest_name ? ` · ${occ.guest_name}` : ''}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Легенда статусов (§4.1) */}
          <div className="cb-legend" data-testid="cb-legend">
            <span className="cb-legend__item">
              <span className="cb-swatch cb-cell--busy" /> Занят
            </span>
            <span className="cb-legend__item">
              <span className="cb-swatch cb-cell--hold" /> Бронь / hold
            </span>
            <span className="cb-legend__item">
              <span className="cb-swatch cb-cell--dirty" /> Уборка / не в строю
            </span>
            <span className="cb-legend__item">
              <span className="cb-swatch cb-cell--free" /> Свободно
            </span>
          </div>
        </>
      )}

    </section>
  );
}

/** Фамилия гостя (первое слово guest_name) для подписи занятой ячейки. */
function guestSurname(guestName: string): string {
  const trimmed = guestName.trim();
  if (trimmed === '') return '';
  return trimmed.split(/\s+/)[0] ?? '';
}

/** Контекст брони для открытия карточки (#3/#7). */
interface OpenStayArg {
  stayId: string;
  guestName: string;
}

/** Одна строка номера: либо ячейки по дням (board), либо полосы (timeline). */
function RoomRow({
  room,
  typeName,
  days,
  view,
  onOpenStay,
}: {
  room: CalendarRoom;
  typeName?: string | undefined;
  days: DayColumn[];
  view: ViewMode;
  onOpenStay: (arg: OpenStayArg) => void;
}): JSX.Element {
  const label = `${room.number}${typeName ? ` · ${typeName}` : ''}`;
  if (view === 'timeline') {
    const bars = buildBars(room, days);
    return (
      <>
        <div className="cb-cell cb-room cb-room-col" role="rowheader" data-testid={`cb-room-${room.number}`}>
          {label}
        </div>
        <div className="cb-timeline-track" style={{ gridColumn: `span ${days.length}` }}>
          <div
            className="cb-timeline-grid"
            style={{ gridTemplateColumns: `repeat(${days.length}, var(--cb-day-col))` }}
          >
            {bars.map((bar) => (
              <button
                type="button"
                key={bar.stayId}
                className={`cb-bar cb-bar--btn cb-cell--${bar.tone}`}
                style={{ gridColumn: `${bar.startIdx + 1} / span ${bar.span}` }}
                title={`${bar.guestName || bar.label} · ${bar.span} дн.`}
                data-testid={`cb-bar-${room.number}-${bar.stayId}`}
                onClick={() => onOpenStay({ stayId: bar.stayId, guestName: bar.guestName })}
              >
                <span className="cb-bar__txt">{bar.guestName || bar.label}</span>
              </button>
            ))}
          </div>
        </div>
      </>
    );
  }
  return (
    <>
      <div className="cb-cell cb-room cb-room-col" role="rowheader" data-testid={`cb-room-${room.number}`}>
        {label}
      </div>
      {days.map((d) => {
        const { tone, label: cl } = cellTone(room, d.iso, d.isToday);
        // Бронь, покрывающая этот день, — чтобы показать фамилию гостя (#3)
        // и сделать ячейку кликабельной → карточка проживания (#7).
        const occ = room.occupancies.find((o) => dayInOccupancy(d.iso, o));
        const surname = occ ? guestSurname(occ.guest_name ?? '') : '';
        const cellText = surname !== '' ? surname : cl;
        const cls = `cb-cell cb-cell--${tone}${d.isToday ? ' is-today' : ''}${d.isWeekend ? ' is-weekend' : ''}`;
        if (occ) {
          return (
            <button
              key={d.iso}
              type="button"
              className={`${cls} cb-cell--btn`}
              title={`${occ.guest_name || cl} · ${room.number}`}
              data-testid={`cb-cell-${room.number}-${d.iso}`}
              onClick={() => onOpenStay({ stayId: occ.stay_id, guestName: occ.guest_name ?? '' })}
            >
              <span className="cb-cell__lbl">{cellText}</span>
            </button>
          );
        }
        return (
          <div
            key={d.iso}
            className={cls}
            role="cell"
            title={cl}
            data-testid={`cb-cell-${room.number}-${d.iso}`}
          >
            {tone !== 'free' && <span className="cb-cell__lbl">{cl}</span>}
          </div>
        );
      })}
    </>
  );
}
